import { Services } from '@sigma-frontend-libs/commons'

import { isEmpty, isValidByOpenApiSchemaProperty, isValidLengthByOpenApiSchemaProperty, isValidPatternByOpenApiSchemaProperty, proxy } from '@/utils'

import { countryRegionsFields, countryRegionsTimeZones, getCountryRegionsBodyProperty, getCountryRegionsLengthRule, getCountryRegionsPatternRule, isCountryRegionsBodyFieldRequired } from '@/components/services/country-regions/utils'
import { validationInfoStates } from '@/components/validationInfo'

import render from './render'

/**
 * Форма создания и изменения записи региона страны.
 * Ограничения полей читаются из `CountryRegionsOpenApiSchema`, состояние
 * каждого ограничения выводится в блоке проверки рядом с полями.
 */
export default {
  name: 'Form',

  mixins: [ proxy({ type: 'object' }) ],

  props: {
    /**
     * Признак создания новой записи.
     * Включает автофокус первого поля и выбирает схему тела запроса создания.
     *
     * @type {boolean}
     * @default false
     */
    isNew: Boolean,

    /**
     * Признак выполнения запроса сохранения.
     *
     * @type {boolean}
     * @default false
     */
    loading: Boolean
  },

  computed: {
    /**
     * Описания полей записи из схемы тела запроса.
     *
     * @returns {Object<string, object>} Описание поля по имени поля.
     */
    fieldsProperties() {
      return countryRegionsFields.reduce((result, fieldName) => {
        result[fieldName] = getCountryRegionsBodyProperty(fieldName, this.isNew)

        return result
      }, {})
    },

    /**
     * Обязательность полей записи по `required` схемы тела запроса.
     *
     * @returns {Object<string, boolean>} Признак обязательного поля по имени поля.
     */
    fieldsRequired() {
      return countryRegionsFields.reduce((result, fieldName) => {
        result[fieldName] = isCountryRegionsBodyFieldRequired(fieldName, this.isNew)

        return result
      }, {})
    },

    /**
     * Группы правил для блока проверки: по каждому полю длина и формат с текущим состоянием.
     *
     * @returns {{ title: string, rules: { text: string, state: string }[] }[]} Группы правил по полям в порядке вывода.
     */
    validationGroups() {
      return countryRegionsFields.map(fieldName => {
        const property = this.fieldsProperties[fieldName]
        const value = this.proxy[fieldName]

        return {
          title: this.getTranslate(`${Services['country-regions']}.labels.${fieldName}`),
          rules: [
            {
              text: getCountryRegionsLengthRule(property),
              state: this.getRuleState(value, isValidLengthByOpenApiSchemaProperty(property, value))
            },
            {
              text: getCountryRegionsPatternRule(fieldName),
              state: this.getRuleState(value, isValidPatternByOpenApiSchemaProperty(property, value))
            }
          ]
        }
      })
    },

    /**
     * Пункты списка часовых поясов.
     *
     * @returns {{ title: string, value: string }[]}
     */
    timeZoneItems() {
      return countryRegionsTimeZones.map(timeZone => {
        return {
          title: timeZone,
          value: timeZone
        }
      })
    },

    /**
     * Признак валидности всей формы.
     * Пустое необязательное поле допустимо, заполненное поле проверяется по схеме.
     *
     * @returns {boolean}
     */
    validation() {
      return countryRegionsFields.every(fieldName => {
        const value = this.proxy[fieldName]
        if (!this.fieldsRequired[fieldName] && isEmpty(value)) {
          return true
        }

        return isValidByOpenApiSchemaProperty(this.fieldsProperties[fieldName], value)
      })
    }
  },

  watch: {
    /**
     * Сообщает родителю об изменении валидности формы.
     */
    validation() {
      this.$emit('validation', this.validation)
    }
  },

  mounted() {
    this.$emit('validation', this.validation)
  },

  methods: {
    /**
     * Возвращает состояние правила для блока проверки.
     *
     * @param {string|undefined} value Значение поля.
     * @param {boolean} valid Признак выполнения правила.
     * @returns {string} Состояние из `validationInfoStates`: `empty` для пустого поля, иначе `valid` или `invalid`.
     */
    getRuleState(value, valid) {
      if (isEmpty(value)) {
        return validationInfoStates.empty
      }
      if (valid) {
        return validationInfoStates.valid
      }

      return validationInfoStates.invalid
    }
  },

  render
}
